/* eslint-disable curly */
/* eslint-disable arrow-body-style */
import { ChangeDetectorRef, Component, ComponentRef, ElementRef, OnInit, ViewChild, ViewContainerRef, AfterViewInit, OnDestroy, DoCheck } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { environment } from "../../../environments/environment";
import { ProductConfig, Category } from "../../data-models/product-config";
import { IProductConfigApiResponse, ProductConfigFactory } from "../../factories/product-config-factory";
import { ProductDetails } from "../../data-models/types";
import { ProductConfigConfigurator360Component } from "../../components/product-config-configurator360/product-config-configurator360.component";
import { ProductDesignConfigurator360Component } from "../../components/product-design-configurator360/product-design-configurator360.component";
import { Instance360Component, Product360InstancesService } from "src/app/services/product-config-360-instances.service";
import { Subscription, Subject, skip, take, tap, concatMap, of } from "rxjs";
import { PimConfigurationKeyMapping, PriceCalculatorService } from "src/app/services/price-calculator.service";
import { ModularConfigFactory, ModularConfig } from "src/app/factories/modular-configuration-factory";
import { UserService } from "src/app/services/authentication/user.service";
import { RedirectService } from "src/app/services/redirect.service";
import { ListPrice } from "src/app/data-models/price.model";
import { CurrencyService } from "src/app/services/currencyService";
import { IntiaroAnalyticsClient } from "src/app/services/analytics/intiaro-analytics.service";
import { utmQrCodeSource, productDesignViewQUeryParams } from "src/app/components/product-design/product-design.component";
import { NavigationService } from "src/app/services/NavigationService.service";
import { NavbarService } from "src/app/services/navbar.service";
import { Db3DApiBackendClient } from "src/app/services/api/db3d-api-backend-client.service";
import { setupConfigurationChangeInitiatedListener } from "src/app/utils/intiaro-configuration-change-initiated";
import { ExtraProductInformation } from "src/app/data-models/extra-product-information.model";
import { ExtraProductInformationService } from "src/app/services/extra-product-information.service";
import { ExtraProductInformationPresentation } from "src/app/utils/extra-product-information-presenter";

type ProductConfigurationInitiatedAnalyticsEventBody = {
  product_configuration_id: string;
  custom_configuration_id?: string;
  design_id?: string;
};

declare var IntiaroAPI: any;

@Component({
  selector: "app-product-config",
  templateUrl: "./product-config.component.html",
  styleUrls: ["./product-config.component.scss"]
})

export class ProductConfigComponent implements OnInit, AfterViewInit, OnDestroy, DoCheck {

  static readonly queryParamsKeyCustomConfigId: string = "custom-config-id";
  static readonly productListPath: string = "product-list";

  @ViewChild("targetEmbed360Instance", { read: ElementRef }) private targetEmbed360Instance!: ElementRef;

  public productConfigId!: string;
  public productConfig!: ProductConfig;
  private productConfigFactory: ProductConfigFactory = new ProductConfigFactory();
  public productDetails: ProductDetails | undefined;
  public isSaveDesignFormVisible = false;
  public customConfigId: string = "";
  public configuratorId: string = "";
  public configurationDetails: Subject<any> = new Subject();
  public isTabsActive = false;

  private modularConfigFactory: ModularConfigFactory = new ModularConfigFactory();
  public productPrice!: number;
  public productPriceCurrency: string = "";

  public priceDetails: ListPrice | null = null;
  public isPriceLoading: boolean = false;

  private isConfigurator360LoadedSubscription!: Subscription;
  public extraProductInformation = new ExtraProductInformationPresentation();
  private extraProductInformationData = new ExtraProductInformation();
  private extraProductInformationConfiguration: unknown = null;
  private extraProductInformationSubscription: Subscription = new Subscription();
  private instance360ComponentRef!: ComponentRef<Instance360Component>;
  private intiaroApiInstance: any = null;
  private configurationChangedSubscription: any;
  private canExitSectionalBuilderSubscription: any;
  private configurationChangeInitiatedCleanup: (() => void) | null = null;
  private pendingPriceRefresh: boolean = false;
  public canExitSectionalBuilder: boolean = true;
  public isProductOptionsReady: boolean = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private product360InstancesService: Product360InstancesService,
    private priceCalculatorService: PriceCalculatorService,
    private userService: UserService,
    private changeDetectorRef: ChangeDetectorRef,
    private viewContainerRef: ViewContainerRef,
    private currencyService: CurrencyService,
    private intiaroAnalyticsClient: IntiaroAnalyticsClient,
    public navigationService: NavigationService,
    private navbarService: NavbarService,
    private db3DApiBackendClient: Db3DApiBackendClient,
    private extraProductInformationService: ExtraProductInformationService
  ) { }

  ngOnInit(): void {
    this.customConfigId = this.activatedRoute.snapshot.queryParamMap.get(ProductConfigComponent.queryParamsKeyCustomConfigId) ?? "";
    this.configuratorId = this.get360ConfiguratorId(!!this.customConfigId);
    this.productConfigId = this.activatedRoute.snapshot.paramMap.get("id") ?? "";

    this.sendIntiaroAnalyticsEvents(this.productConfigId, this.customConfigId);
  }

  /** Reads the same oneshot the configurator loads: the saved configuration when there is one, the product otherwise. */
  private loadExtraProductInformation(): void {
    const information = this.isCustomConfiguration()
      ? this.extraProductInformationService.watchForCustomConfiguration(
        this.productConfig.pim_tenant_id, this.customConfigId, this.productConfig.pim_context ?? ""
      )
      : this.extraProductInformationService.watchForProduct(
        this.productConfig.pim_tenant_id, String(this.productConfig.id), this.productConfig.pim_context ?? ""
      );

    this.extraProductInformationSubscription.add(
      information.subscribe((extraProductInformation: ExtraProductInformation) => {
        this.extraProductInformationData = extraProductInformation;
        this.refreshExtraProductInformation();
      })
    );
  }

  /** A sectional follows its sections, so the block listens to every configuration change. */
  private followConfigurationForExtraProductInformation(): void {
    const controller = this.intiaroApiInstance.productConfigurationController;

    controller.getConfiguration().then((configuration: unknown) => {
      // A change reported in the meantime is newer than this first read.
      if (this.extraProductInformationConfiguration === null) this.updateExtraProductInformationConfiguration(configuration);
    });
    this.extraProductInformationSubscription.add(
      controller.configurationChangedEvent.subscribe((configuration: unknown) => this.updateExtraProductInformationConfiguration(configuration))
    );
  }

  private updateExtraProductInformationConfiguration(configuration: unknown): void {
    if (this.extraProductInformationSubscription.closed) return;

    this.extraProductInformationConfiguration = configuration;
    this.refreshExtraProductInformation();
    this.changeDetectorRef.detectChanges();
  }
  private refreshExtraProductInformation(): void {
    this.extraProductInformation = this.extraProductInformationService.present(
      this.extraProductInformationData,
      this.extraProductInformationConfiguration
    );
  }

  ngDoCheck(): void {
    if (document.querySelector(".intiaro360-wrapper")) {
      if (document.body.classList.contains("intiaro360-full-screen") && document.querySelector(".intiaro360-wrapper.full-screen")) {
        this.navbarService.hide();
      } else {
        this.navbarService.show();
      }
    }
  }

  ngAfterViewInit(): void {
    this.getProductConfigData();
  }

  private sendIntiaroAnalyticsEvents(productConfigId: string, customConfigId: string) {
    const analyticsEventBody: ProductConfigurationInitiatedAnalyticsEventBody = {
      product_configuration_id: productConfigId,
    };
    if (customConfigId) analyticsEventBody.custom_configuration_id = customConfigId;
    const designId = this.activatedRoute.snapshot.queryParamMap.get(productDesignViewQUeryParams.designId);
    if (designId) analyticsEventBody.design_id = designId;

    this.intiaroAnalyticsClient.sendEvent(
      "productConfigurationInitiated",
      analyticsEventBody
    );

    if (this.activatedRoute.snapshot.queryParamMap.get(utmQrCodeSource.name) === utmQrCodeSource.value) {
      this.intiaroAnalyticsClient.sendEvent(
        "productConfigurationInitiatedAfterQrCodeScan",
        analyticsEventBody
      );
    }
  }

  async getProductConfigData(): Promise<void> {

    this.db3DApiBackendClient.getProductConfig(environment.db3dBackendDomain, this.productConfigId).subscribe({
      next: (productConfigData: IProductConfigApiResponse) => {
        if (productConfigData !== undefined) {
          this.productConfig = this.productConfigFactory.createFromBackendApi(productConfigData);
          this.setProductDetailsData(
            this.productConfig.name,
            this.productConfig.brand_name,
            this.productConfig.productVersionName,
            this.productConfig.categories
          );
          this.loadExtraProductInformation();
        }
        this.embed360InstanceComponent();
      },
      error: (err) => {
        if (err.status === 404)
          this.router.navigate([this.navigationService.pageNotFound]);
      },
    });

  }

  public embed360InstanceComponent(): void {

    this.instance360ComponentRef = this.isCustomConfiguration()
      ? this.product360InstancesService.createProductDesignConfigurator360ComponentRef(
        this.customConfigId,
        this.productConfig.pim_tenant_id,
        this.productConfig.pim_context ?? "",
        this.viewContainerRef,
        this.productConfig.isVisual,
        String(this.productConfig.id),
        this.productConfig.thumbnailUrl
      )
      : this.product360InstancesService.createProductConfigConfigurator360ComponentRef(
        this.productConfig.id,
        this.productConfig.pim_tenant_id,
        this.productConfig.pim_context ?? "",
        this.viewContainerRef,
        "product-config-360player",
        this.productConfig.isVisual,
        this.productConfig.thumbnailUrl
      );

    this.changeDetectorRef.detectChanges();
    this.targetEmbed360Instance.nativeElement.appendChild(this.instance360ComponentRef.location.nativeElement);
    this.instance360ComponentRef.changeDetectorRef.detectChanges();

    this.isConfigurator360LoadedSubscription = this.instance360ComponentRef.instance.isConfigurator360Loaded.subscribe((_) => {
      this.intiaroApiInstance = this.instance360ComponentRef.instance.intiaroApiInstance;
      this.setCurrentProductOptions();

      if (this.isPriceEnable()) {
        this.assignCurrencyChange();
      }
      this.addConfigurationChangedListener();
      this.followConfigurationForExtraProductInformation();
    });
  }

  private assignCurrencyChange(): void {

    const oninitPrice = async() => {
      this.isPriceLoading = true;
      if (this.isCustomConfiguration()) {
        this.priceDetails = await this.priceCalculatorService.publicCalculate(PimConfigurationKeyMapping.Custom, this.customConfigId, this.productConfig.pim_tenant_id, this.productConfig.pim_context ?? "", this.productConfig.productVersion);
      } else {
        this.priceDetails = await this.priceCalculatorService.publicCalculate(PimConfigurationKeyMapping.Default, this.productConfigId, this.productConfig.pim_tenant_id, this.productConfig.pim_context ?? "", this.productConfig.productVersion);
      }
      this.isPriceLoading = false;
    };

    const onCurrencyChange = async() => {
      this.isPriceLoading = true;
      this.priceDetails = null;
      const changedConfig = await this.intiaroInstance?.productConfigurationController.getConfiguration();
      if (this.productConfig !== null && changedConfig)
        this.priceDetails = await this.priceCalculatorService.getPriceFromPim(changedConfig, this.productConfig.pim_context ?? "", this.productConfig.pim_tenant_id, this.productConfig.productVersion);
      this.isPriceLoading = false;
    };

    this.currencyService.currencyChange.pipe(
      concatMap((value, index) => index === 0
        ? of(value).pipe(tap(() => oninitPrice()))
        : of(value)
      ), skip(1)).subscribe(() => onCurrencyChange());
  }

  private async setCurrentProductOptions(): Promise<void> {
    const productOptions = await this.intiaroInstance.utilities.getDetailedProductConfiguration();
    this.isTabsActive = true;
    this.isProductOptionsReady = (productOptions?.elements.length > 0);
    if (this.isConfigurationDetailsVisible()) this.configurationDetails.next(productOptions);
    this.changeDetectorRef.detectChanges();
  }

  private hidePrice(): void {
    this.priceDetails = null;
  }

  private addConfigurationChangedListener(): void {
    this.configurationChangeInitiatedCleanup = setupConfigurationChangeInitiatedListener(
      this.targetEmbed360Instance.nativeElement,
      () => {
        this.isPriceLoading = true;
        this.priceDetails = null;
        if (this.isPriceEnable()) this.pendingPriceRefresh = true;
        this.changeDetectorRef.detectChanges();
      },
      async() => {
        if (!this.pendingPriceRefresh) return;
        this.pendingPriceRefresh = false;
        const currentConfig = await this.intiaroInstance?.productConfigurationController.getConfiguration();
        if (!currentConfig) return;
        this.priceDetails = await this.priceCalculatorService.getPriceFromPim(currentConfig, this.productConfig.pim_context ?? "", this.productConfig.pim_tenant_id, this.productConfig.productVersion);
        this.isPriceLoading = false;
        this.changeDetectorRef.detectChanges();
      }
    );

    this.configurationChangedSubscription = this.intiaroInstance.productConfigurationController.configurationChangedEvent.subscribe(async(changedConfig: any) => {
      this.pendingPriceRefresh = false;
      if (changedConfig?.elements && Object.keys(changedConfig.elements).length > 0) {
        this.canExitSectionalBuilder = true;
        this.changeDetectorRef.detectChanges();
      }
      this.setCurrentProductOptions();
      if (this.isPriceEnable()) {
        this.priceDetails = await this.priceCalculatorService.getPriceFromPim(changedConfig, this.productConfig.pim_context ?? "", this.productConfig.pim_tenant_id, this.productConfig.productVersion);
        this.isPriceLoading = false;
      }
    });

    this.canExitSectionalBuilderSubscription = this.intiaroInstance.productConfigurationController.canExitSectionalBuilderEvent?.subscribe((canExit: boolean) => {
      this.canExitSectionalBuilder = canExit;
      this.changeDetectorRef.detectChanges();
    });
  }

  public isPriceEnable(): boolean {
    return this.userService.canUserViewPrices() && !!this.productConfig.pim_context;
  }

  setProductDetailsData(productName: string, brandName: string, programName: string, categories: Array<Category>): void {
    const concatenatedCategories = categories.map((category: any) => category.name).join(", ");
    this.productDetails = {
      productName: productName,
      brandName: brandName,
      programName: programName,
      categories: concatenatedCategories
    };
  }

  get360ConfiguratorId(isCustomConfig: boolean): string {
    return isCustomConfig
      ? ProductDesignConfigurator360Component.defaultConfiguratorId
      : ProductConfigConfigurator360Component.defaultConfiguratorId;
  }

  private get intiaroInstance(): any {
    return this.intiaroApiInstance;
  }

  async saveButtonClicked(): Promise<void> {
    if (this.userService.isLoggedUser()) {
      if (!this.canExitSectionalBuilder) {
        return;
      }
      this.isSaveDesignFormVisible = true;
    } else {
      const configId = await this.intiaroInstance.productConfigurationController.saveConfiguration();
      this.router.navigate([this.navigationService.login], {
        queryParams: { [RedirectService.queryParamKey]: ProductConfigComponent.getPathToCustomisedProduct(this.productConfig.id, configId) },
      });
    }
  }

  static getPathToCustomisedProduct(productId: string, customConfigId: string): string {
    return `${ProductConfigComponent.productListPath}/${productId}?${ProductConfigComponent.queryParamsKeyCustomConfigId}=${customConfigId}`;
  }

  goToProductList(): void {
    this.router.navigate([this.navigationService.productList], {
      queryParams: { [ProductConfigComponent.queryParamsKeyCustomConfigId]: undefined, },
      queryParamsHandling: "merge"
    });
  }

  isCustomConfiguration(): boolean {
    return !!this.customConfigId;
  }

  hideDesignSaveForm(event: any): void {
    if (event) {
      this.isSaveDesignFormVisible = false;
    }
  }

  ngOnDestroy(): void {
    this.isConfigurator360LoadedSubscription?.unsubscribe();
    this.configurationChangedSubscription?.unsubscribe();
    this.canExitSectionalBuilderSubscription?.unsubscribe();
    this.extraProductInformationSubscription.unsubscribe();
    this.configurationChangeInitiatedCleanup?.();
    this.intiaroApiInstance?.destroy();
    // this.productConfig = null;
  }

  public isConfigurationDetailsVisible(): boolean {
    if (this.userService.isLoggedUser()) {
      return this.userService.canViewProductConfigDetails();
    }
    return false;
  }

  public isSaveButtonDisabled(): boolean {
    return !this.canExitSectionalBuilder || !this.isProductOptionsReady;
  }
}
